import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  CardGiftcard as GiftIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { environment } from '../config/environment';
import { getApiHeadersForGet } from '../utils/apiUtils';

interface CouponUsed {
  id: string;
  coupon_code: string;
  coupon_code_id: string;
  coupon_pass_name: string;
  coupon_pass_description: string;
  contest_id: string;
  contest_name: string;
  used_at: string;
  max_uses: number;
  uses_remaining: number;
  ip_address: string;
  user_agent: string;
  user_id: string; // user_id del usuario
}

interface PassCode {
  id: string;
  code: string;
  pass_id: string;
  contest_id: string | null;
  contest_name: string | null;
  used_at: string; // Contiene el user_id cuando se creó desde join-by-name
  created_at: string;
  is_active: boolean;
}

interface CouponsResponse {
  success: boolean;
  user_id?: string;
  email?: string;
  total_coupons_used: number;
  total_pass_codes?: number;
  coupons: CouponUsed[];
  pass_codes?: PassCode[];
}

interface Winner {
  place: number;
  participant: {
    id: string;
    name: string;
    votes: number;
    photo_url: string | null;
    user_id: string | null;
  };
  prize: number;
  is_validated: boolean;
  current_place: number | null;
  current_reward: number | null;
}

interface ContestWinners {
  contest: {
    id: string;
    name: string;
    status: string;
    phase: string;
  };
  winners: Winner[];
  total_participants: number;
  validation_summary: {
    total_winners: number;
    validated_winners: number;
    pending_validation: number;
  };
}

interface WinnersValidationResponse {
  success: boolean;
  valid: boolean;
  contest: {
    id: string;
    name: string;
    status: string;
    phase: string;
  };
  winners: Winner[];
  total_participants: number;
  validation_summary: {
    total_winners: number;
    validated_winners: number;
    pending_validation: number;
  };
}

interface Contest {
  id: string;
  name: string;
  status: string;
  phase: string;
}

const PetWinnersAndCouponsPage: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [couponsData, setCouponsData] = useState<CouponsResponse | null>(null);
  const [contests, setContests] = useState<Contest[]>([]);
  const [winnersByContest, setWinnersByContest] = useState<Record<string, ContestWinners>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContests();
  }, []);

  const loadContests = async () => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests`, {
        headers: getApiHeadersForGet()
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data: Contest[] = await response.json();
      setContests(data);
    } catch (err: any) {
      console.error('Error loading contests:', err);
    }
  };

  const searchCoupons = async () => {
    if (!userId.trim()) {
      setError('Por favor ingresa un User ID');
      return;
    }

    setLoadingCoupons(true);
    setError(null);
    setCouponsData(null);

    try {
      const encodedUserId = encodeURIComponent(userId.trim());
      const response = await fetch(`${environment.apiBaseUrl}/users/${encodedUserId}/coupons`, {
        headers: getApiHeadersForGet()
      });

      if (!response.ok) {
        if (response.status === 404) {
          setCouponsData({
            success: true,
            user_id: userId.trim(),
            total_coupons_used: 0,
            coupons: []
          });
        } else {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
      } else {
        const data: CouponsResponse = await response.json();
        setCouponsData(data);
      }
    } catch (err: any) {
      console.error('Error searching coupons:', err);
      setError(err.message || 'Error buscando cupones');
    } finally {
      setLoadingCoupons(false);
    }
  };

  const loadWinnersForContest = async (contestId: string) => {
    if (winnersByContest[contestId]) {
      return; // Ya está cargado
    }

    setLoadingWinners(true);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/validate-winners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getApiHeadersForGet()
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: WinnersValidationResponse = await response.json();
      if (data.success && data.valid) {
        setWinnersByContest(prev => ({
          ...prev,
          [contestId]: {
            contest: data.contest,
            winners: data.winners,
            total_participants: data.total_participants,
            validation_summary: data.validation_summary
          }
        }));
      }
    } catch (err: any) {
      console.error(`Error loading winners for contest ${contestId}:`, err);
    } finally {
      setLoadingWinners(false);
    }
  };

  const getPlaceIcon = (place: number) => {
    if (place === 1) return '🥇';
    if (place === 2) return '🥈';
    if (place === 3) return '🥉';
    return '🏅';
  };

  const getPlaceColor = (place: number) => {
    if (place === 1) return 'warning';
    if (place === 2) return 'default';
    if (place === 3) return 'secondary';
    return 'primary';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Container className="napc-page" maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3, flexDirection: { xs: 'column', md: 'row' }, mb: 4 }}>
        <Box>
          <Box className="napc-eyebrow">Rewards & proof</Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Ganadores y Cupones
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Revisa beneficios, pases y ganadores con la misma estética cálida del ecosistema NAPC.
          </Typography>
        </Box>
        <Chip
          icon={<ArrowBackIcon />}
          label="Volver"
          onClick={() => navigate(-1)}
          color="primary"
          clickable
        />
      </Box>

      {/* Sección de Búsqueda de Cupones */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <GiftIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" fontWeight="bold">
              Buscar Cupones Usados por Usuario
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="User ID"
              variant="outlined"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchCoupons();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="123"
            />
            <Button
              variant="contained"
              onClick={searchCoupons}
              disabled={loadingCoupons}
              startIcon={loadingCoupons ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: 150 }}
            >
              {loadingCoupons ? 'Buscando...' : 'Buscar'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {couponsData && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Resultados para: <strong>{couponsData.user_id || couponsData.email}</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total de cupones usados: <strong>{couponsData.total_coupons_used}</strong>
                </Typography>
                {couponsData.total_pass_codes !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    Total de Pass Codes: <strong>{couponsData.total_pass_codes}</strong>
                  </Typography>
                )}
              </Box>

              {/* Sección de Cupones */}
              {couponsData.coupons.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No se encontraron cupones usados para este usuario.
                </Alert>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Cupones Usados
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Código de Cupón</strong></TableCell>
                          <TableCell><strong>Nombre del Cupón</strong></TableCell>
                          <TableCell><strong>Concurso</strong></TableCell>
                          <TableCell><strong>Fecha de Uso</strong></TableCell>
                          <TableCell><strong>Usos</strong></TableCell>
                          <TableCell><strong>Participante</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {couponsData.coupons.map((coupon) => (
                          <TableRow key={coupon.id}>
                            <TableCell>
                              <Chip label={coupon.coupon_code} color="primary" size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {coupon.coupon_pass_name || 'N/A'}
                              </Typography>
                              {coupon.coupon_pass_description && (
                                <Typography variant="caption" color="text.secondary">
                                  {coupon.coupon_pass_description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {coupon.contest_name || coupon.contest_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(coupon.used_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {coupon.max_uses - coupon.uses_remaining} / {coupon.max_uses}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {coupon.user_id || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Email del usuario
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Sección de Pass Codes */}
              {couponsData.pass_codes && couponsData.pass_codes.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Pass Codes Vinculados
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Código</strong></TableCell>
                          <TableCell><strong>Concurso</strong></TableCell>
                          <TableCell><strong>Fecha de Creación</strong></TableCell>
                          <TableCell><strong>Estado</strong></TableCell>
                          <TableCell><strong>User ID (en used_at)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {couponsData.pass_codes.map((passCode) => (
                          <TableRow key={passCode.id}>
                            <TableCell>
                              <Chip label={passCode.code} color="secondary" size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {passCode.contest_name || passCode.contest_id || passCode.pass_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(passCode.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={passCode.is_active ? 'Activo' : 'Inactivo'}
                                color={passCode.is_active ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {passCode.used_at || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {passCode.used_at && !passCode.used_at.match(/^\d{4}-\d{2}-\d{2}T/) 
                                  ? 'User ID guardado' 
                                  : 'Fecha de uso'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sección de Ganadores por Concurso */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrophyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" fontWeight="bold">
              Ganadores por Concurso
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {contests.length === 0 ? (
            <Alert severity="info">No hay concursos disponibles.</Alert>
          ) : (
            <Grid container spacing={3}>
              {contests.map((contest) => {
                const winnersData = winnersByContest[contest.id];
                return (
                  <Grid size={{ xs: 12 }} key={contest.id}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {contest.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {contest.id} | Estado: {contest.status} | Fase: {contest.phase}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => loadWinnersForContest(contest.id)}
                          disabled={loadingWinners || !!winnersData}
                        >
                          {winnersData ? 'Cargado' : loadingWinners ? 'Cargando...' : 'Ver Ganadores'}
                        </Button>
                      </Box>

                      {winnersData && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Chip
                              label={`Total Participantes: ${winnersData.total_participants}`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={`Ganadores: ${winnersData.validation_summary.total_winners}`}
                              size="small"
                              color="success"
                            />
                            <Chip
                              label={`Validados: ${winnersData.validation_summary.validated_winners}`}
                              size="small"
                              color="info"
                            />
                            <Chip
                              label={`Pendientes: ${winnersData.validation_summary.pending_validation}`}
                              size="small"
                              color="warning"
                            />
                          </Box>

                          {winnersData.winners.length === 0 ? (
                            <Alert severity="info">No hay ganadores registrados para este concurso.</Alert>
                          ) : (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  sm: 'repeat(2, 1fr)',
                                  md: 'repeat(5, 1fr)'
                                },
                                gap: 2
                              }}
                            >
                              {winnersData.winners.map((winner) => (
                                <Box key={winner.participant.id}>
                                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h4" sx={{ mr: 1 }}>
                                          {getPlaceIcon(winner.place)}
                                        </Typography>
                                        <Chip
                                          label={`${winner.place}° Lugar`}
                                          color={getPlaceColor(winner.place) as any}
                                          size="small"
                                        />
                                        {winner.is_validated && (
                                          <Chip
                                            label="Validado"
                                            color="success"
                                            size="small"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </Box>
                                      <Box
                                        component="img"
                                        src={winner.participant.photo_url || '/Noah.logo.color5.png'}
                                        alt={winner.participant.name}
                                        sx={{
                                          width: '100%',
                                          height: 200,
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                          mb: 1,
                                          flexShrink: 0
                                        }}
                                      />
                                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        {winner.participant.name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {winner.participant.votes} votos
                                      </Typography>
                                      <Divider sx={{ my: 1 }} />
                                      <Typography variant="h6" color="primary" fontWeight="bold">
                                        Premio: ${winner.prize.toFixed(2)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PetWinnersAndCouponsPage;

